import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TableSortLabel,
} from "@mui/material";
import {
  Search,
  Refresh,
  Payment,
  CheckCircle,
  Cancel,
  TrendingUp,
  Email,
  Phone,
  CalendarToday,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { triggerDailyTransactionsDigestApi } from "../../api/adminApi";
import { isEmailAllowedForMembers } from "../../utils/auth";
import DateRangeSelector from "../../components/datePicker";
import "../../App.css";

export default function DailyTransactions() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallerScreen = useMediaQuery(theme.breakpoints.down("md"));

  // Check access on component mount
  const serializedObject = sessionStorage?.getItem("userDetails");
  const userDetails = serializedObject ? JSON.parse(serializedObject) : null;
  const adminEmail = userDetails?.email || "";
  const isAllowed = isEmailAllowedForMembers(adminEmail);

  // Redirect if not allowed
  useEffect(() => {
    if (!isAllowed) {
      navigate("/Dashboard/Home");
    }
  }, [isAllowed, navigate]);

  // State definitions
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [message, setMessage] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [orderBy, setOrderBy] = useState("Created At");
  const [order, setOrder] = useState("desc");

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  
  const [stats, setStats] = useState({
    totalCount: 0,
    successCount: 0,
    failedCount: 0,
    totalVolume: 0,
  });

  // Clean currency values for mathematical operations
  const cleanAmount = (amtStr) => {
    if (!amtStr) return 0;
    return Number(amtStr.toString().replace(/[^0-9.]/g, "")) || 0;
  };

  // Format date values to string formats (MM-DD-YYYY) to match other date filters in the app
  const formatDate = (inputDateVal) => {
    if (!inputDateVal) return "";
    const dateVal = inputDateVal.$d || inputDateVal;
    const dateObj = new Date(dateVal);
    if (!isNaN(dateObj.getTime())) {
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      const year = String(dateObj.getFullYear());
      return `${month}-${day}-${year}`;
    }
    return "";
  };

  // Main API trigger to load/trigger the digest
  const fetchDigestData = (isManualTrigger = false) => {
    if (!isAllowed) return;
    setLoading(true);

    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);

    triggerDailyTransactionsDigestApi(formattedStart, formattedEnd, isManualTrigger)
      .then((res) => {
        setLoader(false);
        setLoading(false);
        if (res?.statusCode === 404 || res?.statusCode === 401) {
          navigate("/");
        } else if (res?.success) {
          const list = res?.data?.transactions || [];
          const sortedList = [...list].sort((a, b) => new Date(b["Created At"]) - new Date(a["Created At"]));
          // Add unique ids to each row
          const formattedList = sortedList.map((item, idx) => ({
            ...item,
            id: item["Transaction ID"] && item["Transaction ID"] !== "-" && item["Transaction ID"] !== "N/A"
              ? item["Transaction ID"]
              : `tx-${idx}-${Date.now()}`,
          }));

          setTransactions(formattedList);
          setFilteredTransactions(formattedList);
          
          if (isManualTrigger) {
            setMessage(res.message || "Daily digest triggered and email sent successfully.");
          } else {
            setMessage("");
          }

          // Calculate statistics dynamically
          const totalCount = formattedList.length;
          const successCount = formattedList.filter((t) =>
            t.Status?.toUpperCase().includes("SUCCESS")
          ).length;
          const failedCount = formattedList.filter((t) =>
            t.Status?.toUpperCase().includes("FAILED")
          ).length;
          const totalVolume = formattedList.reduce(
            (sum, t) => sum + cleanAmount(t.Amount),
            0
          );

          setStats({
            totalCount,
            successCount,
            failedCount,
            totalVolume,
          });
        } else {
          setMessage(res?.message || "Failed to load transactions digest.");
        }
      })
      .catch((err) => {
        setLoader(false);
        setLoading(false);
        console.error("Failed to load daily transactions digest:", err);
        setMessage("An error occurred while fetching the transactions digest.");
      });
  };

  // Fetch when dates change
  useEffect(() => {
    if (isAllowed) {
      fetchDigestData(false);
    }
  }, [startDate, endDate, isAllowed]);

  // Filter logic based on local states
  useEffect(() => {
    let result = [...transactions];

    if (statusFilter) {
      result = result.filter(
        (t) => t.Status?.toUpperCase() === statusFilter.toUpperCase()
      );
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t["User Name"]?.toLowerCase().includes(q) ||
          t["Email"]?.toLowerCase().includes(q) ||
          t["Mobile"]?.toLowerCase().includes(q) ||
          t["Transaction ID"]?.toLowerCase().includes(q) ||
          t["Order ID"]?.toLowerCase().includes(q) ||
          t["Plan"]?.toLowerCase().includes(q)
      );
    }

    // Sorting logic based on selected column
    result.sort((a, b) => {
      let valueA = a[orderBy];
      let valueB = b[orderBy];

      if (orderBy === "Amount") {
        valueA = cleanAmount(a["Amount"]);
        valueB = cleanAmount(b["Amount"]);
      } else if (orderBy === "Created At") {
        valueA = new Date(a["Created At"]).getTime();
        valueB = new Date(b["Created At"]).getTime();
      } else {
        valueA = String(valueA || "").toLowerCase();
        valueB = String(valueB || "").toLowerCase();
      }

      if (valueA < valueB) {
        return order === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

    setFilteredTransactions(result);
  }, [searchQuery, statusFilter, transactions, orderBy, order]);

  // Handle Search Input Change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle Status Filter Dropdown Change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // Handle Date Pickers Changes
  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  // Status badging styles
  const getStatusChipStyles = (status) => {
    const s = status?.toUpperCase() || "";
    if (s.includes("SUCCESS (PENDING")) {
      return { bg: "#FFF1CC", text: "#B78103", border: "1px solid #FFE0B2" };
    }
    if (s.includes("SUCCESS")) {
      return { bg: "#E2FCD5", text: "#227200", border: "1px solid #C8E6C9" };
    }
    if (s.includes("FAILED")) {
      return { bg: "#FFE7E6", text: "#D32F2F", border: "1px solid #FFCDD2" };
    }
    // INITIATED or others
    return { bg: "#D0ECFF", text: "#1890FF", border: "1px solid #B3E5FC" };
  };

  // Render loader if screen load is in progress or user is not allowed
  if (loader || !isAllowed) {
    return (
      <div className="ayoub">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Daily Transactions | Duco Insure</title>
      </Helmet>

      <Grid container sx={{ p: "25px" }} spacing={3}>
        {/* Banner/Header section */}
        <Grid item xs={12}>
          <Card
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #7a00bcff 0%, #9205e9ff 100%)",
              color: "white",
              borderRadius: "16px",
              boxShadow: "0px 6px 20px rgba(74, 0, 114, 0.15)",
              display: "flex",
              flexDirection: isSmallerScreen ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isSmallerScreen ? "flex-start" : "center",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1, fontFamily: "Open Sans" }}>
                Daily Transactions
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, maxWidth: "600px", fontSize: "14px" }}>
                Generate the daily transactions email digest on-demand and view all logs from the selected date range. Emails are automatically routed to the registered admin inbox.
              </Typography>
            </Box>
            {/* <Button
              variant="contained"
              disabled={loading}
              onClick={() => fetchDigestData(true)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
              sx={{
                bgcolor: "white",
                color: "#4A0072",
                fontWeight: "bold",
                px: 3,
                py: 1.5,
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "15px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#F3E5F5",
                  transform: "translateY(-1px)",
                },
                "&.Mui-disabled": {
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                  color: "#4A0072",
                }
              }}
            >
              {loading ? "Triggering..." : "Trigger & Refresh Digest"}
            </Button> */}
          </Card>
        </Grid>

        {/* Message notification if any */}
        {message && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                bgcolor: message.includes("Failed") || message.includes("error") ? "#FFE7E6" : "#E2FCD5",
                color: message.includes("Failed") || message.includes("error") ? "#D32F2F" : "#227200",
                fontWeight: "bold",
                border: message.includes("Failed") || message.includes("error") ? "1px solid #FFCDD2" : "1px solid #C8E6C9",
              }}
            >
              {message}
            </Box>
          </Grid>
        )}

        {/* Statistics Dashboard widgets */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            {/* Widget 1: Total Transactions */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
                  border: "1px solid #E6D4F0",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.02)" }
                }}
              >
                <Box>
                  <Typography align="center" variant="body2" sx={{ color: "#8E8D9A", fontWeight: "600", mb: 0.5 }}>
                    Total Transactions
                  </Typography>
                  <Typography align="center" variant="h3" sx={{ fontWeight: "bold", color: "#4A0072" }}>
                    {stats.totalCount}
                  </Typography>
                </Box>
                {/* <Box sx={{ bgcolor: "#F5E6FF", p: 1.5, borderRadius: "12px" }}>
                  <Payment sx={{ color: "#8E24AA", fontSize: 30 }} />
                </Box> */}
              </Paper>
            </Grid>

            {/* Widget 2: Successful */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
                  border: "1px solid #E2FCD5",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.02)" }
                }}
              >
                <Box>
                  <Typography align="center" variant="body2" sx={{ color: "#8E8D9A", fontWeight: "600", mb: 0.5 }}>
                    Successful Fulfilled
                  </Typography>
                  <Typography  align="center" variant="h3" sx={{ fontWeight: "bold", color: "#2E7D32" }}>
                    {stats.successCount}
                  </Typography>
                </Box>
                {/* <Box sx={{ bgcolor: "#E8F5E9", p: 1.5, borderRadius: "12px" }}>
                  <CheckCircle sx={{ color: "#4CAF50", fontSize: 30 }} />
                </Box> */}
              </Paper>
            </Grid>

            {/* Widget 3: Failed / Reconciliation Errors */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
                  border: "1px solid #FFE7E6",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.02)" }
                }}
              >
                <Box>
                  <Typography align="center" variant="body2" sx={{ color: "#8E8D9A", fontWeight: "600", mb: 0.5 }}>
                    Failed / Mismatch
                  </Typography>
                  <Typography align="center" variant="h3" sx={{ fontWeight: "bold", color: "#C62828" }}>
                    {stats.failedCount}
                  </Typography>
                </Box>
                {/* <Box sx={{ bgcolor: "#FFEBEE", p: 1.5, borderRadius: "12px" }}>
                  <Cancel sx={{ color: "#F44336", fontSize: 30 }} />
                </Box> */}
              </Paper>
            </Grid>

            {/* Widget 4: Total Volume */}
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.03)",
                  border: "1px solid #E3F2FD",
                  transition: "transform 0.2s",
                  "&:hover": { transform: "scale(1.02)" }
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ color: "#8E8D9A", fontWeight: "600", mb: 0.5 }}>
                    Digest Volume
                  </Typography>
                  <Typography align="center" variant="h3" sx={{ fontWeight: "bold", color: "#1565C0" }}>
                    ₹{stats.totalVolume.toLocaleString("en-IN")}
                  </Typography>
                </Box>
                {/* <Box sx={{ bgcolor: "#E3F2FD", p: 1.5, borderRadius: "12px" }}>
                  <TrendingUp sx={{ color: "#2196F3", fontSize: 30 }} />
                </Box> */}
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Filter and Table Container */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: "16px", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)" }}>
            <Box
              sx={{
                p: 3,
                display: "flex",
                flexDirection: isSmallerScreen ? "column" : "row",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Left Side: Filter & Date range options */}
              <Box sx={{ display: "flex", flexDirection: isSmallerScreen ? "column" : "row", gap: 2, alignItems: "center", width: isSmallerScreen ? "100%" : "auto" }}>
                <FormControl size="small" sx={{ minWidth: 150, width: isSmallerScreen ? "100%" : "auto" }}>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    displayEmpty
                    sx={{
                      borderRadius: "8px",
                      bgcolor: "#F8F9FA",
                      fontFamily: "Open Sans",
                    }}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="SUCCESS (Pending Fulfillment)">Success (Pending Fulfillment)</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                    <MenuItem value="INITIATED">Initiated</MenuItem>
                  </Select>
                </FormControl>

                <DateRangeSelector
                  endDate={endDate}
                  setEndDate={setEndDate}
                  setStartDate={setStartDate}
                  startDate={startDate}
                  handleStartDateChange={handleStartDateChange}
                  handleEndDateChange={handleEndDateChange}
                />
              </Box>

              {/* Right Side: Search bar */}
              <TextField
                size="small"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search name, email, txn id..."
                inputProps={{ maxLength: 100 }}
                sx={{ width: isSmallerScreen ? "100%" : "300px" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#8E8D9A" }} />
                    </InputAdornment>
                  ),
                  style: {
                    borderRadius: "8px",
                    backgroundColor: "#F8F9FA",
                    fontFamily: "Open Sans",
                  },
                }}
              />
            </Box>

            <Divider />

            {/* Table Presentation */}
            <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead sx={{ bgcolor: "#FCF8FF" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", color: "#83868B", fontFamily: "Open Sans" }}>
                      <TableSortLabel
                        active={orderBy === "User Name"}
                        direction={orderBy === "User Name" ? order : "asc"}
                        onClick={() => handleSort("User Name")}
                        sx={{
                          "&.MuiTableSortLabel-active": { color: "#4A0072" },
                          "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": { color: "#4A0072 !important" }
                        }}
                      >
                        Customer Details
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#83868B", fontFamily: "Open Sans" }}>
                      <TableSortLabel
                        active={orderBy === "Transaction ID"}
                        direction={orderBy === "Transaction ID" ? order : "asc"}
                        onClick={() => handleSort("Transaction ID")}
                        sx={{
                          "&.MuiTableSortLabel-active": { color: "#4A0072" },
                          "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": { color: "#4A0072 !important" }
                        }}
                      >
                        Transaction Details
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#83868B", fontFamily: "Open Sans" }}>
                      <TableSortLabel
                        active={orderBy === "Amount"}
                        direction={orderBy === "Amount" ? order : "asc"}
                        onClick={() => handleSort("Amount")}
                        sx={{
                          "&.MuiTableSortLabel-active": { color: "#4A0072" },
                          "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": { color: "#4A0072 !important" }
                        }}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#83868B", fontFamily: "Open Sans" }}>
                      <TableSortLabel
                        active={orderBy === "Status"}
                        direction={orderBy === "Status" ? order : "asc"}
                        onClick={() => handleSort("Status")}
                        sx={{
                          "&.MuiTableSortLabel-active": { color: "#4A0072" },
                          "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": { color: "#4A0072 !important" }
                        }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", color: "#83868B", fontFamily: "Open Sans" }}>
                      <TableSortLabel
                        active={orderBy === "Created At"}
                        direction={orderBy === "Created At" ? order : "asc"}
                        onClick={() => handleSort("Created At")}
                        sx={{
                          "&.MuiTableSortLabel-active": { color: "#4A0072" },
                          "&.MuiTableSortLabel-active .MuiTableSortLabel-icon": { color: "#4A0072 !important" }
                        }}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                        <Typography variant="body1" sx={{ color: "#8E8D9A", fontStyle: "italic", fontFamily: "Open Sans" }}>
                          No transactions found matching your search or filters in the current digest.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTransactions.map((row) => {
                      const statusStyle = getStatusChipStyles(row.Status);
                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            transition: "background-color 0.2s",
                            "&:hover": { bgcolor: "#FAF6FE" },
                          }}
                        >
                          {/* Col 1: Customer Details */}
                          <TableCell>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                                {row["User Name"]}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#8E8D9A" }}>
                                <Email sx={{ fontSize: 13 }} />
                                <Typography variant="caption" sx={{ fontSize: "11.5px" }}>{row.Email}</Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#8E8D9A" }}>
                                <Phone sx={{ fontSize: 13 }} />
                                <Typography variant="caption" sx={{ fontSize: "11.5px" }}>{row.Mobile}</Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Col 2: Transaction / Order ID */}
                          <TableCell>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#8E8D9A", fontWeight: "600" }}>TXN ID:</Typography>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#4A0072", fontWeight: "600" }}>
                                  {row["Transaction ID"]}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Typography variant="caption" sx={{ color: "#8E8D9A", fontWeight: "600" }}>ORDER:</Typography>
                                <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#555" }}>
                                  {row["Order ID"]}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Col 3: Plan / Amount */}
                          <TableCell>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                              <Chip
                                label={row.Plan || "N/A"}
                                size="small"
                                sx={{
                                  bgcolor: "#E6D4F0",
                                  color: "#4A0072",
                                  fontWeight: "bold",
                                  alignSelf: "flex-start",
                                  borderRadius: "4px",
                                }}
                              />
                              <Typography variant="body1" sx={{ fontWeight: "bold", color: "#333", mt: 0.5 }}>
                                {row.Amount}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Col 4: Status Badge */}
                          <TableCell>
                            <Chip
                              label={row.Status}
                              size="small"
                              sx={{
                                bgcolor: statusStyle.bg,
                                color: statusStyle.text,
                                border: statusStyle.border,
                                fontWeight: "bold",
                                borderRadius: "6px",
                                fontSize: "11.5px",
                                py: 1.5,
                              }}
                            />
                          </TableCell>

                          {/* Col 5: Created At */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#555" }}>
                              <CalendarToday sx={{ fontSize: 14, color: "#8E8D9A" }} />
                              <Typography variant="body2" sx={{ fontSize: "12.5px" }}>
                                {row["Created At"]}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
